import { defineComponent as _defineComponent } from 'vue'
import { Heater, HeaterState, MachineStatus, SpindleState, Tool } from "@duet3d/objectmodel";
import { computed, ref } from "vue";

import i18n from "@/i18n";
import store from "@/store";
import { getHeaterColor } from "@/utils/colors";
import { DisconnectedError, getErrorMessage } from "@/utils/errors";
import { log, LogType } from "@/utils/logging";
import { displaySensorValue } from "@/utils/display";


export default /*#__PURE__*/_defineComponent({
  __name: 'ToolRows',
  emits: ["resetHeaterFault"],
  setup(__props, { emit }: { emit: ({
    (e: "resetHeaterFault", heater: number): void
}), expose: any, slots: any, attrs: any }) {



const disabled = computed<boolean>(() => store.getters["uiFrozen"] || [MachineStatus.pausing, MachineStatus.processing, MachineStatus.resuming, MachineStatus.simulating].includes(store.state.machine.model.state.status));

// Tool display
const toolsToDisplay = computed<Array<Tool>>(() => {
    if (!store.state.machine.settings.groupTools) {
        return store.state.machine.model.tools.filter(tool => tool !== null) as Array<Tool>;
    }

    const tools: Array<Tool> = [];
    for (const item of store.state.machine.model.tools) {
        if (item !== null) {
            let equalToolFound = false;

            for (let i = 0; i < tools.length; i++) {
                const tool = tools[i];
                if ((item.extruders.length === tool.extruders.length && item.extruders.every((extruder, index) => extruder === tool.extruders[index])) &&
                    (item.heaters.length === tool.heaters.length && item.heaters.every((heater, index) => heater === tool.heaters[index])) &&
                    (item.offsets.length === tool.offsets.length && item.offsets.every((offset, index) => offset === tool.offsets[index])) &&
                    item.spindle === tool.spindle)
                {
                    // Tool is identical
                    equalToolFound = true;
                    if (item.number === store.state.machine.model.state.currentTool) {
                        // If another tool is selected, prefer the displayed tool
                        tools[i] = item;
                    }
                }
            }

            if (!equalToolFound) {
                tools.push(item);
            }
        }
    }
    return tools;
});

const currentTool = computed(() => store.state.machine.model.state.currentTool), busyTool = ref<Tool | null>(null);
const selectedToolClass = computed(() => store.state.settings.darkTheme ? "grey darken-3" : "blue lighten-5");

function isToolCollapsed(tool: Tool) {
    if (toolsToDisplay.value.length < store.state.machine.model.tools.length) {
        for (const item of store.state.machine.model.tools) {
            if (item !== null && item !== tool) {
                if ((item.extruders.length === tool.extruders.length && item.extruders.every((extruder, index) => extruder === tool.extruders[index])) &&
                    (item.heaters.length === tool.heaters.length && item.heaters.every((heater, index) => heater === tool.heaters[index])) &&
                    (item.offsets.length === tool.offsets.length && item.offsets.every((offset, index) => offset === tool.offsets[index])) &&
                    item.spindle === tool.spindle)
                {
                    return true;
                }
            }
        }
    }
    return false;
}

function getCollapsedTools(tool: Tool) {
    const tools: Array<Tool> = [];
    for (const item of store.state.machine.model.tools) {
        if (item !== null &&
            (item.extruders.length === tool.extruders.length && item.extruders.every((extruder, index) => extruder === tool.extruders[index])) &&
            (item.heaters.length === tool.heaters.length && item.heaters.every((heater, index) => heater === tool.heaters[index])) &&
            (item.offsets.length === tool.offsets.length && item.offsets.every((offset, index) => offset === tool.offsets[index])) &&
            item.spindle === tool.spindle)
        {
            // Tool is identical
            tools.push(item);
        }
    }
    return tools;
}

function isCollapsedToolBusy(tool: Tool) {
    return getCollapsedTools(tool).includes(busyTool.value as Tool);
}

function isToolBusy(tool: Tool) {
    return isToolCollapsed(tool) ? isCollapsedToolBusy(tool) : (busyTool.value === tool);
}

function getToolIcon(tool: Tool) {
    if (tool !== null) {
        if (tool.extruders.length > 0) {
            if (store.state.machine.model.heat.heaters.some((heater, heaterIndex) => (heater !== null) && (heater.state === HeaterState.fault) && tool.heaters.includes(heaterIndex))) {
                return "mdi-printer-3d-nozzle-alert";
            }
            return "mdi-printer-3d-nozzle";
        }
        if (tool.spindle >= 0) {
            return "mdi-saw-blade";
        }
        if (tool.name.toLowerCase().includes("laser")) {
            // TODO the object model does not report if a laser is mapped to a tool
            return "mdi-star-four-points-circle-outline";
        }
    }
    return null;
}

// Tool caption
const toolChangeParameter = computed<string>(() => store.getters["machine/settings/toolChangeParameter"]);

async function toolClick(tool: Tool) {
    if (disabled.value || busyTool.value !== null) {
        return;
    }

    busyTool.value = tool;
    try {
        if (store.state.machine.model.state.currentTool === tool.number) {
            // Deselect current tool
            await store.dispatch("machine/sendCode", "T-1" + toolChangeParameter.value);
        } else {
            // Select new tool
            await store.dispatch("machine/sendCode", `T${tool.number}${toolChangeParameter.value}`);
        }
    } catch (e) {
        if (!(e instanceof DisconnectedError)) {
            log(LogType.error, getErrorMessage(e));
        }
    }
    busyTool.value = null;
}

// Filament management
const loadingFilament = ref(false), filamentDialogShown = ref(false), filamentRunMacros = ref(true), filamentDialogTool = ref<Tool | null>(null);

function getFilament(tool: Tool) {
    if ((tool.filamentExtruder >= 0) && (tool.filamentExtruder < store.state.machine.model.move.extruders.length)) {
        return store.state.machine.model.move.extruders[tool.filamentExtruder].filament;
    }
    return null;
}

function canLoadFilament(tool: Tool) {
    return (tool.filamentExtruder >= 0) && (tool.filamentExtruder < store.state.machine.model.move.extruders.length);
}

async function showFilamentDialog(tool: Tool, runMacros: bool) {
    if (busyTool.value !== null || disabled.value) {
        return;
    }

    filamentDialogTool.value = tool;
    filamentRunMacros.value = runMacros;
    filamentDialogShown.value = true;
}

async function unloadFilament(tool: Tool) {
    if (busyTool.value !== null || disabled.value) {
        return;
    }

    busyTool.value = tool;
    try {
        let code = "";
        if (currentTool.value !== tool.number) {
            code = `T${tool.number}\n`;
        }
        code += "M702";
        await store.dispatch("machine/sendCode", code);
    } finally {
        busyTool.value = null;
    }
}

// Tool heaters
function getToolHeaters(tool: Tool) {
    const heaters = store.state.machine.model.heat.heaters;
    const toolHeaters = tool.heaters
        .filter(heaterIndex => (heaterIndex >= 0) && (heaterIndex < heaters.length) && (heaters[heaterIndex] !== null))
        .map(heaterIndex => heaters[heaterIndex]);
    return (toolHeaters.length > 0) ? toolHeaters : [null];
}

function getHeaterClasses(heater: number) {
    const classes = [getHeaterColor(heater)];
    if (disabled.value) {
        classes.push("disabled-heater");
    }
    return classes;
}

function getHeaterName(heater: Heater | null, heaterIndex: number) {
    if ((heater !== null) && (heater.sensor >= 0) && (heater.sensor < store.state.machine.model.sensors.analog.length)) {
        const sensor = store.state.machine.model.sensors.analog[heater.sensor];
        if ((sensor !== null) && sensor.name) {
            const matches = /(.*)\[(.*)\]$/.exec(sensor.name);
            if (matches) {
                return matches[1];
            }
            return sensor.name;
        }
    }
    return i18n.t("panel.tools.heater", [heaterIndex]);
}

function getHeaterValue(heater: Heater | null) {
    if ((heater !== null) && (heater.sensor >= 0) && (heater.sensor < store.state.machine.model.sensors.analog.length)) {
        const sensor = store.state.machine.model.sensors.analog[heater.sensor];
        if (sensor !== null) {
            return displaySensorValue(sensor);
        }
    }
    return i18n.t("generic.noValue");
}

async function toolHeaterClick(tool: Tool, heater: Heater) {
    if (disabled.value || isToolBusy(tool)) {
        return;
    }

    switch (heater.state) {
        case HeaterState.off:		// Off -> Active
            await store.dispatch("machine/sendCode", `M568 P${tool.number} A2`);
            break;

        case HeaterState.standby:	// Standby -> Off
            await store.dispatch("machine/sendCode", `M568 P${tool.number} A0`);
            break;

        case HeaterState.active:	// Active -> Standby
            await store.dispatch("machine/sendCode", `M568 P${tool.number} A1`);
            break;

        case HeaterState.fault:		// Fault -> Ask for reset
            emit("resetHeaterFault", store.state.machine.model.heat.heaters.indexOf(heater));
            break;
    }
}

// Spindles
function getSpindle(tool: Tool) {
    return (tool.spindle >= 0) && (tool.spindle < store.state.machine.model.spindles.length) ? store.state.machine.model.spindles[tool.spindle] : null;
}

function getSpindleSpeed(tool: Tool) {
    const spindle = getSpindle(tool);
    return (spindle !== null && spindle.current !== null) ? ((spindle.state === SpindleState.reverse) ? -spindle.current : spindle.current) : 0;
}

return { __sfc: true,emit, disabled, toolsToDisplay, currentTool, busyTool, selectedToolClass, isToolCollapsed, getCollapsedTools, isCollapsedToolBusy, isToolBusy, getToolIcon, toolChangeParameter, toolClick, loadingFilament, filamentDialogShown, filamentRunMacros, filamentDialogTool, getFilament, canLoadFilament, showFilamentDialog, unloadFilament, getToolHeaters, getHeaterClasses, getHeaterName, getHeaterValue, toolHeaterClick, getSpindle, getSpindleSpeed }
}

})